import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";

import dfSubDays from "date-fns/subDays";

import { formatDate, getDateByTimeZone } from "@/lib/date";

import Delivery from "@/Models/Delivery";
import DeliveryCollectedLog from "@/Models/DeliveryCollectedLog";
import DeliveryStatusLog from "@/Models/DeliveryStatusLog";
import SentBackLog from "@/Models/SentBackLog";
import api from "../Api";

export const QUERY_STATUS = {
  ALL: "ALL",
  RECEIPTED: "RECEIPTED",
  PICKUP_SCHEDULED: "PICKUP_SCHEDULED",
  PICKUP_COMPLETED: "PICKUP_COMPLETED",
  PICKUP_COMPLETED_TYPE_PICKUP: "PICKUP_COMPLETED_TYPE_PICKUP",
  WAREHOUSED: "WAREHOUSED",
  DELIVERY_ALLOCATED: "DELIVERY_ALLOCATED",
  DELIVERY_STARTED: "DELIVERY_STARTED",
  DELIVERY_COMPLETED: "DELIVERY_COMPLETED",
  SENT_BACK_COMPLETED: "SENT_BACK_COMPLETED",
  LOST_COMPLETED: "LOST",
  TYPE_PICKUP: "TYPE_PICKUP",
  TYPE_RETURN: "TYPE_RETURN",
  CANCELED: "CANCELED",
  ACCIDENT: "ACCIDENT",
  SENDER_ADDRESS_ERROR: "SENDER_ADDRESS_ERROR",
  SENDER_ADDRESS_ERROR_CANCELED: "SENDER_ADDRESS_ERROR_CANCELED",
  RECEIVER_ADDRESS_ERROR: "RECEIVER_ADDRESS_ERROR",
  RECEIVER_ADDRESS_ERROR_CANCELED: "RECEIVER_ADDRESS_ERROR_CANCELED",
  DELAYED: "DELAYED",
  POSTPONED: "POSTPONED",
  ADDRESS_NOT_SUPPORTED: "ADDRESS_NOT_SUPPORTED",
  ADDRESS_NOT_SUPPORTED_CANCELED: "ADDRESS_NOT_SUPPORTED_CANCELED",
  ADDRESS_NOT_SUPPORTED_WITH_BUSAN: "ADDRESS_NOT_SUPPORTED_WITH_BUSAN", // drx 전용
  ADDRESS_NOT_SUPPORTED_WITH_BUSAN_CANCELED:
    "ADDRESS_NOT_SUPPORTED_WITH_BUSAN_CANCELED", // drx 전용
};

export const QUERY_TYPES = {
  NORMAL: "1",
  PICKUP: "2",
  RETURN: "3",
};

export const CONSTANTS = {
  FETCH_ALL: "FETCH_ALL",
  FETCH_BY_ID: "FETCH_BY_ID",
  FETCH_COLLECTED_LOGS_BY_ID: "FETCH_COLLECTED_LOGS_BY_ID",
  FETCH_DELAYED_DELIVERIES_BY_ID: "FETCH_DELAYED_DELIVERIES_BY_ID",
  FETCH_STATUS_LOGS_BY_ID: "FETCH_STATUS_LOGS_BY_ID",
  FETCH_SENTBACK_LOGS_BY_ID: "FETCH_SENTBACK_LOGS_BY_ID",
  FETCH_BATCHES_BULK_UPLOADED: "FETCH_BATCHES_BULK_UPLOADED",
  SET_COUNT: "SET_COUNT",
  SET_DELIVERIES_FOR_WAYBILL: "SET_DELIVERIES_FOR_WAYBILL",
  SET_QUERY: "SET_QUERY",
};

const zonedDateYesterday = dfSubDays(getDateByTimeZone(), 1);
const zonedDateToday = getDateByTimeZone();
const initDateFrom = formatDate(zonedDateYesterday);
const initDateTo = formatDate(zonedDateToday);

const INITIAL_QUERY = {
  dateFrom: initDateFrom,
  dateTo: initDateTo,
  page: 1,
  pageSize: 20,
  status: "ALL",
  query: "",
  region: "",
  dateType: "RECEIPT_DATE",
  senderName: "",
  filterByLevel: "only-me",
  sortKey: "receiptDate",
  sortType: "DESC",
};

const INITIAL_STATE = {
  count: {
    all: 0,
    receipted: 0,
    pickupScheduled: 0,
    deliveryAllocated: 0,
    deliveryStarted: 0,
    deliveryCompleted: 0,
    sentBack: 0,
    lost: 0,
    typePickup: 0,
    typeReturn: 0,
    canceled: 0,
    accident: 0,
    senderAddressError: 0,
    senderAddressErrorCanceled: 0,
    receiverAddressError: 0,
    receiverAddressErrorCanceled: 0,
    delayed: 0,
    postponed: 0,
    addressNotSupported: 0,
    addressNotSupportedCanceled: 0,
    pickupCompletedTypePickup: 0,
    addressNotSupportedWithBusan: 0,
    addressNotSupportedWithBusanCanceled: 0,
  },
  deliveries: [],
  deliveriesForWaybill: [],
  batchesBulkUploaded: [],
  delivery: {},
  totalCount: 0,
  pageCount: 0,
  query: INITIAL_QUERY,
  collectedLogs: [],
  delayedDeliveries: [],
  statusLogs: [],
  sentBackLogs: [],
};

const reducer = (state, action) => {
  switch (action.type) {
    case CONSTANTS.FETCH_ALL:
      return {
        ...state,
        deliveries: action.deliveries,
        totalCount: action.totalCount,
        pageCount: action.pageCount,
      };
    case CONSTANTS.FETCH_BY_ID:
      return {
        ...state,
        delivery: action.delivery,
      };
    case CONSTANTS.FETCH_COLLECTED_LOGS_BY_ID:
      return {
        ...state,
        collectedLogs: action.collectedLogs,
      };
    case CONSTANTS.FETCH_DELAYED_DELIVERIES_BY_ID:
      return {
        ...state,
        delayedDeliveries: action.delayedDeliveries,
      };
    case CONSTANTS.FETCH_STATUS_LOGS_BY_ID:
      return {
        ...state,
        statusLogs: action.statusLogs,
      };
    case CONSTANTS.FETCH_SENTBACK_LOGS_BY_ID:
      return {
        ...state,
        sentBackLogs: action.sentBackLogs,
      };
    case CONSTANTS.FETCH_BATCHES_BULK_UPLOADED:
      return {
        ...state,
        batchesBulkUploaded: action.batchesBulkUploaded,
      };
    case CONSTANTS.SET_COUNT:
      return {
        ...state,
        count: {
          ...state.count,
          all:
            action.count.receipted +
            action.count.pickupScheduled +
            action.count.deliveryAllocated +
            action.count.deliveryStarted +
            action.count.deliveryCompleted +
            action.count.sentBack +
            action.count.postponed +
            action.count.lost,
          // action.count.typePickup +
          // action.count.typeReturn,
          ...action.count,
        },
      };
    case CONSTANTS.SET_DELIVERIES_FOR_WAYBILL:
      return {
        ...state,
        deliveriesForWaybill: action.deliveriesForWaybill,
      };
    case CONSTANTS.SET_QUERY:
      return {
        ...state,
        query: {
          ...state.query,
          ...action.query,
        },
      };
    default:
      return INITIAL_STATE;
  }
};

export const Context = createContext(INITIAL_STATE);

export const Provider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  return (
    <Context.Provider value={{ dispatch, state }}>{children}</Context.Provider>
  );
};

export const useDeliveriesStore = () => {
  const { dispatch, state } = useContext(Context);

  const callbackDrx = useCallback(
    async (deliveries) => {
      return await Promise.allSettled(
        deliveries.map((delivery) =>
          api.put(`/deliveries/${delivery.bookId}/cancel/callbacks`),
        ),
      );
    },
    [api.put],
  );

  const cancel = useCallback(
    async (bookId) => await api.put(`/deliveries/${bookId}/cancel`),
    [api.put],
  );

  const cancelBulk = useCallback(
    async (deliveries) => {
      return await Promise.allSettled(
        deliveries.map((delivery) =>
          api.put(`/deliveries/${delivery.bookId}/cancel`),
        ),
      );
    },
    [api.put],
  );

  const copyBulk = useCallback(
    async (deliveries) => {
      return await Promise.allSettled(
        deliveries.map((delivery) =>
          api.post(`/deliveries/${delivery.bookId}/copy`),
        ),
      );
    },
    [api.post],
  );

  const countByStatus = useCallback(
    async ({ isDrx = false }) => {
      let promises = [
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.RECEIPTED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.PICKUP_SCHEDULED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.DELIVERY_ALLOCATED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.DELIVERY_STARTED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.DELIVERY_COMPLETED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.SENT_BACK_COMPLETED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.TYPE_PICKUP,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.TYPE_RETURN,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.CANCELED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.ACCIDENT,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.SENDER_ADDRESS_ERROR,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.SENDER_ADDRESS_ERROR_CANCELED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.RECEIVER_ADDRESS_ERROR,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.RECEIVER_ADDRESS_ERROR_CANCELED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.DELAYED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.POSTPONED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.ADDRESS_NOT_SUPPORTED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.ADDRESS_NOT_SUPPORTED_CANCELED,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.PICKUP_COMPLETED,
          types: QUERY_TYPES.PICKUP,
        }),
        api.get("/deliveries/count", {
          ...state.query,
          status: QUERY_STATUS.LOST_COMPLETED,
        }),
      ];

      if (isDrx) {
        promises.push(
          api.get("/deliveries/count", {
            ...state.query,
            status: QUERY_STATUS.ADDRESS_NOT_SUPPORTED_WITH_BUSAN,
          }),
        );
        // promises.push(
        //   api.get("/deliveries", {
        //     ...state.query,
        //     status: QUERY_STATUS.CANCELED,
        //   }),
        // );
      }

      Promise.all(promises).then((results) => {
        let count = {
          receipted: results[0]?.count,
          pickupScheduled: results[1]?.count,
          deliveryAllocated: results[2]?.count,
          deliveryStarted: results[3]?.count,
          deliveryCompleted: results[4]?.count,
          sentBack: results[5]?.count,
          typePickup: results[6]?.count,
          typeReturn: results[7]?.count,
          canceled: results[8]?.count,
          accident: results[9]?.count,
          senderAddressError: results[10]?.count,
          senderAddressErrorCanceled: results[11]?.count,
          receiverAddressError: results[12]?.count,
          receiverAddressErrorCanceled: results[13]?.count,
          delayed: results[14]?.count,
          postponed: results[15]?.count,
          addressNotSupported: results[16]?.count,
          addressNotSupportedCanceled: results[17]?.count,
          pickupCompletedTypePickup: results[18]?.count,
          lost: results[19]?.count || 0,
        };

        if (isDrx) {
          count.addressNotSupportedWithBusan = results[20]?.count || 0;
          // const canceledDeliveries = results[22]?.rows || [];
          // count.addressNotSupportedWithBusanCanceled =
          //   canceledDeliveries.filter((d) => d.status !== null).length;
        }

        dispatch({
          type: CONSTANTS.SET_COUNT,
          count,
        });
      });

      return true;
    },
    [api.get, dispatch, state.query],
  );

  const download = useCallback(
    (deliveries) => {
      let query = {
        ...state.query,
        sortKey: "receiptDate",
        sortType: "ASC",
      };

      if (deliveries?.length > 0) {
        query.bookIds = deliveries.map((d) => d.bookId);
      }

      query.downloadType = "print";
      return api.postForDownload("/deliveries/download", query);

      // return api.get("/deliveries/download", query);
    },
    [api.postForDownload, state.query],
  );

  const downloadAddress = useCallback(
    (deliveries) => {
      let query = {
        ...state.query,
        sortKey: "receiptDate",
        sortType: "ASC",
      };

      if (deliveries?.length > 0) {
        query.bookIds = deliveries.map((d) => d.bookId);
      }

      query.downloadType = "print";
      return api.postForDownload("/deliveries/download/address", query);
    },
    [api.postForDownload, state.query],
  );

  const fetchAll = useCallback(
    async ({ agPostalCodes }) => {
      let query = {
        ...state.query,
      };

      if (query.status === QUERY_STATUS.PICKUP_COMPLETED_TYPE_PICKUP) {
        query.status = QUERY_STATUS.PICKUP_COMPLETED;
        query.types = QUERY_TYPES.PICKUP;
      }

      const { rows, totalCount } = await api.get("/deliveries", query);

      dispatch({
        type: CONSTANTS.FETCH_ALL,
        deliveries: rows.map((d) => new Delivery(d, agPostalCodes)),
        totalCount,
        pageCount:
          Math.floor(totalCount / state.query.pageSize) +
          (totalCount % state.query.pageSize ? 1 : 0),
      });
    },
    [dispatch, state.query],
  );

  const fetchByIdOnTheFly = useCallback(
    async (bookId) => {
      const response = await api.get(`/deliveries/${bookId}`);

      dispatch({
        type: CONSTANTS.FETCH_BY_ID,
        delivery: new Delivery(response),
      });

      return response;
    },
    [dispatch],
  );

  const fetchAddressById = useCallback(
    async (bookId) => {
      return await api.get(`/deliveries/${bookId}/addresses`);
    },
    [api.get],
  );

  const fetchCollectedLogsById = useCallback(
    async (bookId) => {
      const response = await api.get(`/delivery-collection-logs`, {
        bookId,
      });

      dispatch({
        type: CONSTANTS.FETCH_COLLECTED_LOGS_BY_ID,
        collectedLogs: response.rows.map((d) => new DeliveryCollectedLog(d)),
      });

      return response;
    },
    [dispatch],
  );

  const fetchDelayedDeliveriesById = useCallback(
    async (bookId) => {
      const response = await api.get(
        `/deliveries/${bookId}/delayed-deliveries`,
      );

      dispatch({
        type: CONSTANTS.FETCH_DELAYED_DELIVERIES_BY_ID,
        delayedDeliveries: response.rows || [],
      });

      return response;
    },
    [api.get, dispatch],
  );

  const fetchDoneToday = useCallback(async () => {
    const response = await api.get("/deliveries/done-today");

    return response?.rows.map((d) => new Delivery(d));
  }, [api.get]);

  const fetchNotPrinted = useCallback(async () => {
    const response = await api.get("/deliveries/not-printed", state.query);

    return response?.rows.map((d) => new Delivery(d));
  }, [api.get, state.query]);

  const fetchBatchesBulkUploaded = useCallback(async () => {
    const response = await api.get("/deliveries/worksheet/batch");

    dispatch({
      type: CONSTANTS.FETCH_BATCHES_BULK_UPLOADED,
      batchesBulkUploaded:
        response?.rows.filter((d) => d.hasResult === true) || [],
    });

    return response;
  }, [api.get]);

  const fetchStatusLogsById = useCallback(
    async (bookId) => {
      const response = await api.get(
        `/deliveries/${bookId}/delivery-status-logs`,
      );

      dispatch({
        type: CONSTANTS.FETCH_STATUS_LOGS_BY_ID,
        statusLogs: response.rows.map((d) => new DeliveryStatusLog(d)),
      });

      return response;
    },
    [dispatch],
  );

  const fetchSentBackLogsById = useCallback(async (bookId) => {
    const response = await api.get(`/deliveries/${bookId}/sent-back-logs`, {
      page: 1,
      pageSize: 10,
    });

    dispatch({
      type: CONSTANTS.FETCH_SENTBACK_LOGS_BY_ID,
      sentBackLogs: response?.rows.map((d) => new SentBackLog(d)),
    });

    return response;
  });

  const printedBulk = useCallback(
    async (deliveries) => {
      return await Promise.allSettled(
        deliveries.map((delivery) =>
          api.put(`/deliveries/${delivery.bookId}/print`),
        ),
      );
    },
    [api.put],
  );

  const returnBulk = useCallback(
    async (deliveries) => {
      return await Promise.allSettled(
        deliveries.map((delivery) =>
          api.post(`/deliveries/${delivery.bookId}/returns`),
        ),
      );
    },
    [api.put],
  );

  const setDeliveriesForWaybill = useCallback(
    (_deliveriesForWaybill) => {
      dispatch({
        type: CONSTANTS.SET_DELIVERIES_FOR_WAYBILL,
        deliveriesForWaybill: _deliveriesForWaybill,
      });
    },
    [dispatch],
  );

  const setQuery = useCallback(
    (query = {}) => {
      dispatch({ type: CONSTANTS.SET_QUERY, query });
    },
    [dispatch],
  );

  return {
    state,
    callbackDrx,
    cancel,
    cancelBulk,
    copyBulk,
    countByStatus,
    download,
    downloadAddress,
    fetchAll,
    fetchAddressById,
    fetchByIdOnTheFly,
    fetchCollectedLogsById,
    fetchDelayedDeliveriesById,
    fetchDoneToday,
    fetchNotPrinted,
    fetchBatchesBulkUploaded,
    fetchStatusLogsById,
    fetchSentBackLogsById,
    printedBulk,
    returnBulk,
    setDeliveriesForWaybill,
    setQuery,
  };
};
